import React from 'react';
import { Box, Typography } from '@mui/material';
import { getOmerDayHebrew } from '../../omerUtils';

// ── תאי הרשת ─────────────────────────────────────────────────────────────────
const DayCell = ({ day, today, isPast }) => {
  const isToday = day === today;

  return (
    <Box sx={{
      width: '100%',
      aspectRatio: '1',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '6px',
      border: isToday
        ? '2px solid #e6c45e'
        : '1px solid rgba(30,58,95,0.12)',
      background: isToday
        ? '#0f1e35'
        : isPast
          ? 'rgba(30,58,95,0.06)'
          : 'transparent',
      transition: 'all 0.2s',
      boxShadow: isToday ? '0 2px 12px rgba(230,196,94,0.25)' : 'none',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {isToday && (
        <Box sx={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(circle at center, rgba(230,196,94,0.15), transparent 70%)',
        }} />
      )}
      <Typography sx={{
        fontFamily: 'Frank Ruhl Libre, serif',
        fontSize: 'clamp(0.4rem, 1.5vw, 0.65rem)',
        color: isToday ? '#e6c45e' : isPast ? '#475569' : '#94a3b8',
        fontWeight: isToday ? 700 : 400,
        lineHeight: 1,
        zIndex: 1,
        userSelect: 'none',
      }}>
        {getOmerDayHebrew(day)}
      </Typography>
    </Box>
  );
};

// ── שורת שבוע ────────────────────────────────────────────────────────────────
const WEEK_SEFIROT = ['חֶסֶד', 'גְּבוּרָה', 'תִּפְאֶרֶת', 'נֶצַח', 'הוֹד', 'יְסוֹד', 'מַלְכוּת'];

// ── תפילות אחרי ספירה — Grid ─────────────────────────────────────────────────
const AfterCountSection = ({ section }) => (
  <Box sx={{ mt: 1.5, borderTop: '1px solid #e2e8f0', pt: 1.25, width: '100%' }}>
    <Typography sx={{
      fontFamily: 'Frank Ruhl Libre, serif',
      fontSize: '0.58rem',
      color: '#94a3b8',
      letterSpacing: '0.1em',
      textAlign: 'center',
      mb: 0.5,
    }}>
      — {section.title} —
    </Typography>
    {section.lines.map((line) => (
      <Typography key={line.id} sx={{
        fontFamily: 'Frank Ruhl Libre, serif',
        fontSize: '0.6rem',
        color: '#475569',
        lineHeight: 1.85,
        textAlign: 'center',
        direction: 'rtl',
      }}>
        {line.text}
      </Typography>
    ))}
  </Box>
);

// ── תבנית רשת 7×7 ────────────────────────────────────────────────────────────
const Grid = ({
  omerDay, isOmerSeason,
  isPreview, previewCountdown,
  mainCountText, subCountText,
  showBracha, brachaText,
  showLeshemYichud, leshemYichudLines,
  showAfterCount, afterCountSections,
  offSeasonMessage,
  title, showTitle,
  bgColor,
}) => {
  const bg = bgColor || '#f8f4e8';

  if (!isOmerSeason) {
    return (
      <Box sx={{
        background: bg,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        direction: 'rtl',
      }}>
        <Typography sx={{ color: '#94a3b8', fontFamily: 'Frank Ruhl Libre, serif', fontSize: '0.9rem' }}>
          {offSeasonMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      background: bg,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '10px 10px 12px',
      direction: 'rtl',
      overflowY: 'auto',
      '&::-webkit-scrollbar': { width: 3 },
      '&::-webkit-scrollbar-thumb': { background: '#e2e8f0', borderRadius: 2 },
    }}>
      {showTitle && (
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, serif',
          fontSize: '0.65rem',
          color: '#8b6914',
          letterSpacing: '0.12em',
          mb: 0.75,
        }}>
          {title}
        </Typography>
      )}

      {/* באנר ספירה לאחור */}
      {isPreview && previewCountdown && (
        <Box sx={{
          mb: 0.75, px: 1.5, py: 0.5,
          background: 'rgba(139,105,20,0.08)',
          border: '1px solid rgba(139,105,20,0.2)',
          borderRadius: 1,
          width: '100%',
        }}>
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, serif',
            fontSize: '0.68rem',
            color: '#8b6914',
            textAlign: 'center',
            direction: 'rtl',
          }}>
            {previewCountdown}
          </Typography>
        </Box>
      )}

      {/* כותרות שמות ספירות */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', width: '100%', mb: 0.25 }}>
        {WEEK_SEFIROT.map((s) => (
          <Box key={s} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{
              fontFamily: 'Frank Ruhl Libre, serif',
              fontSize: 'clamp(0.35rem, 1.1vw, 0.5rem)',
              color: '#8b6914',
              textAlign: 'center',
              opacity: 0.75,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
            }}>
              {s.split('׳')[0].substring(0, 3)}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* רשת 7×7 */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px', width: '100%' }}>
        {Array.from({ length: 49 }, (_, i) => i + 1).map((day) => (
          <DayCell
            key={day}
            day={day}
            today={omerDay}
            isPast={day < omerDay}
          />
        ))}
      </Box>

      {/* ברכה */}
      {showBracha && (
        <Box sx={{
          mt: 1.25,
          px: 1.5, py: 0.75,
          background: 'rgba(30,58,95,0.05)',
          borderRadius: 1,
          border: '1px solid rgba(30,58,95,0.1)',
          width: '100%',
        }}>
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, serif',
            fontSize: '0.62rem',
            color: '#1e293b',
            lineHeight: 2,
            textAlign: 'center',
            direction: 'rtl',
          }}>
            {brachaText}
          </Typography>
        </Box>
      )}

      {/* שורת ספירה */}
      <Typography sx={{
        fontFamily: 'Frank Ruhl Libre, serif',
        fontSize: 'clamp(0.75rem, 2.5vw, 1rem)',
        color: '#1e3a5f',
        mt: showBracha ? 0.75 : 1.25,
        fontWeight: 600,
        textAlign: 'center',
        direction: 'rtl',
      }}>
        {mainCountText}
      </Typography>

      {subCountText && (
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, serif',
          fontSize: 'clamp(0.62rem, 1.8vw, 0.82rem)',
          color: '#475569',
          mt: 0.25,
          textAlign: 'center',
          direction: 'rtl',
        }}>
          {subCountText}
        </Typography>
      )}

      {/* תפילות אחרי ספירה */}
      {showAfterCount && afterCountSections.length > 0 && (
        <Box sx={{ width: '100%' }}>
          {afterCountSections.map((section) => (
            <AfterCountSection key={section.id} section={section} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default Grid;
